import React, { useState, useEffect, useCallback } from 'react';
import {
  Form, Input, InputNumber, Select, Button, Card, Row, Col,
  message, Space, Typography, DatePicker,
} from 'antd';
import { SaveOutlined, ArrowLeftOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../../api/axios';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Türkçe sayı-yazı dönüşümü
const birler = ['', 'bir', 'iki', 'üç', 'dört', 'beş', 'altı', 'yedi', 'sekiz', 'dokuz'];
const onlar = ['', 'on', 'yirmi', 'otuz', 'kırk', 'elli', 'altmış', 'yetmiş', 'seksen', 'doksan'];
const binler = ['', 'bin', 'milyon', 'milyar', 'trilyon'];

function uchaneYaz(n) {
  if (n === 0) return '';
  let s = '';
  const y = Math.floor(n / 100);
  const on = Math.floor((n % 100) / 10);
  const bir = n % 10;
  if (y > 0) s += (y === 1 ? '' : birler[y]) + 'yüz';
  if (on > 0) s += onlar[on];
  if (bir > 0) s += birler[bir];
  return s;
}

function turkceYaz(sayi) {
  if (!sayi && sayi !== 0) return '';
  const num = Math.round(Number(sayi));
  if (num === 0) return 'sıfır';
  let sonuc = '';
  let gruplar = [];
  let n = num;
  while (n > 0) { gruplar.push(n % 1000); n = Math.floor(n / 1000); }
  for (let i = gruplar.length - 1; i >= 0; i--) {
    const g = gruplar[i];
    if (g === 0) continue;
    let gs = uchaneYaz(g);
    if (i === 1 && g === 1) gs = '';
    sonuc += gs + binler[i];
  }
  return sonuc + ' Türk Lirası';
}

function downloadBlob(blobData, headers) {
  const cd = headers['content-disposition'] || '';
  let filename = 'sozlesme.docx';
  const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
  const plainMatch = cd.match(/filename="([^"]+)"/i);
  if (utf8Match) filename = decodeURIComponent(utf8Match[1]);
  else if (plainMatch) filename = plainMatch[1];

  const url = window.URL.createObjectURL(new Blob([blobData]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

async function handleBlobError(err, fallback) {
  if (err.response?.data instanceof Blob) {
    try {
      const text = await err.response.data.text();
      const parsed = JSON.parse(text);
      return parsed.error || fallback;
    } catch { return fallback; }
  }
  return err.response?.data?.error || fallback;
}

const ProjeSatisForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [wordLoading, setWordLoading] = useState(false);
  const [acLoading, setAcLoading] = useState(false);
  const [projeler, setProjeler] = useState([]);
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const fetchProjeler = useCallback(async () => {
    try {
      const res = await api.get('/projeler');
      const arr = Array.isArray(res.data) ? res.data : res.data.data || [];
      setProjeler(arr.filter((p) => p.aktif !== false));
    } catch { /* ignore */ }
  }, []);

  const fetchData = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    try {
      const res = await api.get(`/projesatis/${id}`);
      const item = res.data.data || res.data;
      if (item.sozlesmeTarihi) item.sozlesmeTarihi = dayjs(item.sozlesmeTarihi);
      form.setFieldsValue(item);
    } catch {
      message.error('Kayıt yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [id, isEdit, form]);

  useEffect(() => {
    fetchProjeler();
    fetchData();
  }, [fetchProjeler, fetchData]);

  const handleSatisBedeliChange = (val) => {
    if (val != null) form.setFieldValue('satisBedeliYazi', turkceYaz(val));
  };

  const handlePesinatChange = (val) => {
    if (val != null) form.setFieldValue('pesinatYazi', turkceYaz(val));
  };

  const onFinish = async (values) => {
    setSaving(true);
    try {
      const payload = {
        ...values,
        sozlesmeTarihi: values.sozlesmeTarihi ? values.sozlesmeTarihi.toISOString() : null,
      };
      if (isEdit) {
        await api.put(`/projesatis/${id}`, payload);
        message.success('Proje satışı güncellendi');
      } else {
        await api.post('/projesatis', payload);
        message.success('Proje satışı eklendi');
      }
      navigate('/proje-satis');
    } catch (err) {
      message.error(err.response?.data?.message || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleKaydetIndir = async () => {
    setWordLoading(true);
    try {
      const res = await api.get(`/projesatis/generate-word/${id}`, { responseType: 'blob' });
      downloadBlob(res.data, res.headers);
    } catch (err) {
      const msg = await handleBlobError(err, 'Word dosyası oluşturulamadı');
      message.error(msg);
    } finally {
      setWordLoading(false);
    }
  };

  const handleAc = async () => {
    setAcLoading(true);
    try {
      const res = await api.get(`/projesatis/saved-word/${id}`, { responseType: 'blob' });
      downloadBlob(res.data, res.headers);
    } catch (err) {
      const msg = await handleBlobError(err, 'Kaydedilmiş dosya bulunamadı');
      message.error(msg);
    } finally {
      setAcLoading(false);
    }
  };

  const sectionStyle = { marginBottom: 16, borderRadius: 8 };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/proje-satis')}>Geri</Button>
            <Title level={4} style={{ margin: 0 }}>
              {isEdit ? 'Proje Satışı Düzenle' : 'Yeni Proje Satışı'}
            </Title>
          </Space>
        </Col>
        {isEdit && (
          <Col>
            <Space>
              <Button icon={<EyeOutlined />} loading={acLoading} onClick={handleAc}>Aç</Button>
              <Button type="primary" icon={<DownloadOutlined />} loading={wordLoading} onClick={handleKaydetIndir}>
                Kaydet ve İndir
              </Button>
            </Space>
          </Col>
        )}
      </Row>

      <Form form={form} layout="vertical" onFinish={onFinish}>
        {/* Proje Seçimi */}
        <Card title="Proje" style={sectionStyle} loading={loading}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="projeId" label="Proje Seç" rules={[{ required: true, message: 'Proje seçin' }]}>
                <Select placeholder="Proje seçin" showSearch
                  filterOption={(input, option) =>
                    (option?.children || '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {projeler.map((p) => (
                    <Option key={p.id} value={p.id}>{p.projeAdi}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Birim Bilgileri */}
        <Card title="Birim Bilgileri" style={sectionStyle}>
          <Row gutter={16}>
            <Col xs={12} md={5}>
              <Form.Item name="blokNo" label="Blok No">
                <Input placeholder="A, B, 1..." />
              </Form.Item>
            </Col>
            <Col xs={12} md={5}>
              <Form.Item name="daireNo" label="Daire No">
                <Input placeholder="Daire no" />
              </Form.Item>
            </Col>
            <Col xs={12} md={5}>
              <Form.Item name="katNo" label="Kat No">
                <InputNumber style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
            </Col>
            <Col xs={12} md={5}>
              <Form.Item name="brutMetrekare" label="Brüt m²">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="netMetrekare" label="Net m²">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Alıcı Bilgileri */}
        <Card title="Alıcı Bilgileri" style={sectionStyle}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="aliciAdi" label="Ad Soyad" rules={[{ required: true, message: 'Zorunlu' }]}>
                <Input placeholder="Alıcı adı soyadı" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="aliciTc" label="TC Kimlik No">
                <Input placeholder="TC Kimlik No" maxLength={11} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="aliciTelefon" label="Telefon">
                <Input placeholder="05xx xxx xxxx" />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item name="aliciEmail" label="E-Posta">
                <Input placeholder="email@domain.com" type="email" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item name="aliciAdres" label="Adres">
                <TextArea rows={2} placeholder="Alıcı adresi" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Finansal Bilgiler */}
        <Card title="Finansal Bilgiler" style={sectionStyle}>
          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Form.Item name="satisBedeli" label="Satış Bedeli (₺)">
                <InputNumber
                  min={0} style={{ width: '100%' }}
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                  parser={(v) => v.replace(/\./g, '')}
                  onChange={handleSatisBedeliChange}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="satisBedeliYazi" label="Satış Bedeli (Yazı)">
                <Input placeholder="Otomatik dolar" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Form.Item name="pesinat" label="Peşinat (₺)">
                <InputNumber
                  min={0} style={{ width: '100%' }}
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                  parser={(v) => v.replace(/\./g, '')}
                  onChange={handlePesinatChange}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="pesinatYazi" label="Peşinat (Yazı)">
                <Input placeholder="Otomatik dolar" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item name="odemePlani" label="Ödeme Planı / Koşulları">
                <TextArea rows={4} placeholder="Ödeme planını buraya girin..." />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Sözleşme Bilgileri */}
        <Card title="Sözleşme Bilgileri" style={sectionStyle}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="sozlesmeNo" label="Sözleşme No">
                <Input placeholder="PS-2024-001" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="sozlesmeTarihi" label="Sözleşme Tarihi">
                <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="durum" label="Durum" initialValue="Aktif">
                <Select>
                  <Option value="Aktif">Aktif</Option>
                  <Option value="İptal">İptal</Option>
                  <Option value="Tamamlandı">Tamamlandı</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Row justify="end" style={{ marginTop: 8 }}>
          <Space>
            <Button onClick={() => navigate('/proje-satis')}>İptal</Button>
            <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
              {isEdit ? 'Güncelle' : 'Kaydet'}
            </Button>
          </Space>
        </Row>
      </Form>
    </div>
  );
};

export default ProjeSatisForm;
